import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Fieldset,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { semanticColor } from "@how/design-system";
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import {
  isLinkOnlyAddress,
  isLivestreamAddress,
  isLivestreamProviderAvailable,
  LIVESTREAM_PROVIDER_LABELS,
  livestreamProviderOf,
  richTextFromMarkup,
  richTextToMarkup,
  streamAddressFromPaste,
  youtubeHandle,
  type LivestreamProvider,
  type RichText,
} from "@how/website-core";
import { emptyItem, STREAM_ADDRESS_HELP, streamAddressProblem, type Field } from "./block-fields";
import type { FormRow } from "./form-queries";
import type { AssetRow } from "./website-queries";

type Values = Record<string, unknown>;

/** A campus as the block form offers it. */
export interface CampusChoice {
  readonly id: string;
  readonly name: string;
  /** Null: the church's own zone. */
  readonly timeZone: string | null;
}

/** What some fields need from the church beyond the block: its campuses and zone, and the YouTube channel finder. */
export interface BlockFieldsContext {
  readonly campuses: readonly CampusChoice[];
  readonly churchTimeZone: string;
  /**
   * Finds the channel a YouTube handle address names (the resolve-youtube-channel function): the channel id, or an
   * Error whose message is the sentence to show. Absent, the form asks staff to paste the channel's address instead.
   */
  readonly findYoutubeChannel?: (address: string) => Promise<string>;
  /** The church's series and sermons, for the sermon feature block; absent without sermons and media. */
  readonly sermonChoices?:
    | {
        readonly series: readonly SermonChoice[];
        readonly sermons: readonly SermonChoice[];
      }
    | undefined;
}

const NO_CONTEXT: BlockFieldsContext = { campuses: [], churchTimeZone: "America/New_York" };

/** A sermon or a series as the sermon feature block's form offers it (ADR 0061). */
export interface SermonChoice {
  readonly id: string;
  readonly label: string;
}

export interface BlockFieldsProps {
  readonly fields: readonly Field[];
  readonly value: Values;
  readonly onChange: (next: Values) => void;
  readonly assets: readonly AssetRow[];
  readonly forms: readonly FormRow[];
  readonly disabled: boolean;
  readonly context?: BlockFieldsContext;
}

/** Sets a key, removing it when the value is empty so optional fields stay absent rather than blank. */
function withValue(values: Values, key: string, next: unknown): Values {
  if (next === undefined || next === "") {
    return Object.fromEntries(Object.entries(values).filter(([k]) => k !== key));
  }
  return { ...values, [key]: next };
}

const str = (value: unknown): string => (typeof value === "string" ? value : "");

/** A group of fields (a button, an image) is named like a field label, not like faint help text. */
const LEGEND = {
  legend: { fontWeight: 600, fontSize: "var(--mantine-font-size-sm)", marginBottom: 4 },
};

/** The form for one block (or one item of a list inside a block), driven by the field list for its type. */
export function BlockFields({
  fields,
  value,
  onChange,
  assets,
  forms,
  disabled,
  context = NO_CONTEXT,
}: BlockFieldsProps) {
  return (
    <Stack gap="sm">
      {fields.map((field) => {
        if ("showIf" in field && !field.showIf(value)) {
          return null;
        }
        if (field.kind === "stream") {
          // The stream address changes more than its own key (the provider it belongs to, a handle's channel).
          return (
            <StreamControl
              key={field.key}
              field={field}
              block={value}
              onBlockChange={onChange}
              disabled={disabled}
              context={context}
            />
          );
        }
        return (
          <FieldControl
            key={field.key}
            field={field}
            value={value[field.key]}
            values={value}
            onChange={(next) => {
              onChange(withValue(value, field.key, next));
            }}
            assets={assets}
            forms={forms}
            disabled={disabled}
            context={context}
          />
        );
      })}
    </Stack>
  );
}

interface FieldControlProps {
  readonly field: Field;
  readonly value: unknown;
  /** The block's (or the list item's) other values, for a field whose check depends on them. */
  readonly values?: Values;
  readonly onChange: (next: unknown) => void;
  readonly assets: readonly AssetRow[];
  readonly forms: readonly FormRow[];
  readonly disabled: boolean;
  readonly context?: BlockFieldsContext;
}

function FieldControl({
  field,
  value,
  values = {},
  onChange,
  assets,
  forms,
  disabled,
  context = NO_CONTEXT,
}: FieldControlProps) {
  switch (field.kind) {
    case "text": {
      const problem = field.check?.(str(value), values) ?? null;
      return (
        <TextInput
          label={field.label}
          required={field.required === true}
          disabled={disabled}
          {...(field.description === undefined ? {} : { description: field.description })}
          {...(problem === null ? {} : { error: problem })}
          value={str(value)}
          onChange={(event) => {
            const next = event.currentTarget.value;
            onChange(next);
          }}
        />
      );
    }
    case "textarea":
      return (
        <Textarea
          label={field.label}
          required={field.required === true}
          disabled={disabled}
          autosize
          minRows={2}
          value={str(value)}
          onChange={(event) => {
            const next = event.currentTarget.value;
            onChange(next);
          }}
        />
      );
    case "select":
      return (
        <Select
          label={field.label}
          disabled={disabled}
          allowDeselect={false}
          data={field.options.map((o) => ({
            value: o.value,
            label: o.label,
            ...(o.disabled === true ? { disabled: true } : {}),
          }))}
          renderOption={({ option }) => {
            const badge = field.options.find((o) => o.value === option.value)?.badge;
            return badge === undefined ? (
              option.label
            ) : (
              <Group gap="xs" wrap="nowrap" justify="space-between" w="100%">
                <span>{option.label}</span>
                <Badge size="sm" variant="light" color={semanticColor("neutral")}>
                  {badge}
                </Badge>
              </Group>
            );
          }}
          value={str(value) || null}
          onChange={(next) => {
            onChange(next ?? undefined);
          }}
        />
      );
    case "time":
      return (
        <TextInput
          type="time"
          label={field.label}
          disabled={disabled}
          value={str(value)}
          onChange={(event) => {
            const next = event.currentTarget.value;
            onChange(next);
          }}
        />
      );
    case "number":
      return (
        <NumberInput
          label={field.label}
          disabled={disabled}
          min={field.min}
          max={field.max}
          step={field.step ?? 5}
          {...(field.description === undefined ? {} : { description: field.description })}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="blur"
          {...(field.suffix === undefined
            ? {}
            : {
                suffix: ` ${value === 1 && field.suffixOne !== undefined ? field.suffixOne : field.suffix}`,
              })}
          value={typeof value === "number" ? value : ""}
          onChange={(next) => {
            onChange(typeof next === "number" ? next : undefined);
          }}
        />
      );
    case "checkbox":
      return (
        <Checkbox
          label={field.label}
          disabled={disabled}
          {...(field.description === undefined ? {} : { description: field.description })}
          checked={value === true}
          onChange={(event) => {
            const next = event.currentTarget.checked;
            onChange(next);
          }}
        />
      );
    case "lines":
      return (
        <Textarea
          label={field.label}
          disabled={disabled}
          autosize
          minRows={2}
          value={Array.isArray(value) ? value.join("\n") : ""}
          onChange={(event) => {
            const next = event.currentTarget.value.split("\n");
            onChange(next.length === 1 && next[0] === "" ? [] : next);
          }}
          onBlur={() => {
            if (Array.isArray(value))
              onChange(value.map(String).filter((line) => line.trim().length > 0));
          }}
        />
      );
    case "form":
      return (
        <Select
          label={field.label}
          description="Made under Website, Forms. What visitors send arrives there."
          placeholder={forms.length === 0 ? "No forms yet" : "Choose a form"}
          disabled={disabled || forms.length === 0}
          allowDeselect={false}
          data={forms.map((form) => ({ value: form.id, label: form.name }))}
          value={str(value) || null}
          onChange={(next) => {
            onChange(next ?? undefined);
          }}
        />
      );
    case "link":
      return (
        <LinkControl
          field={field}
          value={value}
          onChange={onChange}
          forms={forms}
          disabled={disabled}
        />
      );
    case "image":
      return (
        <ImageControl
          field={field}
          value={value}
          onChange={onChange}
          assets={assets}
          forms={forms}
          disabled={disabled}
        />
      );
    case "richtext":
      return (
        <RichTextControl
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case "list":
      return (
        <ListControl
          field={field}
          value={value}
          onChange={onChange}
          assets={assets}
          forms={forms}
          disabled={disabled}
          context={context}
        />
      );
    case "campus":
      return (
        <CampusControl
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          context={context}
        />
      );
    case "stream":
      // Drawn by BlockFields itself, which can change the block's other keys.
      return null;
    case "sermonSource":
      return (
        <SermonSourceControl
          label={field.label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          context={context}
        />
      );
  }
}

/**
 * Which sermons the sermon feature block draws (ADR 0061): the newest, a series's newest, or one sermon. The series
 * and sermons are the church's own; a published sermon only shows on the site once it is published.
 */
function SermonSourceControl({
  label,
  value,
  onChange,
  disabled,
  context,
}: {
  readonly label: string;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly disabled: boolean;
  readonly context: BlockFieldsContext;
}) {
  const source =
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : { mode: "latest" };
  const mode =
    source["mode"] === "series" || source["mode"] === "sermon" ? source["mode"] : "latest";
  const choices = context.sermonChoices ?? { series: [], sermons: [] };
  return (
    <Stack gap="xs">
      <Select
        label={label}
        disabled={disabled}
        allowDeselect={false}
        data={[
          { value: "latest", label: "The newest sermons" },
          {
            value: "series",
            label: "The newest in one series",
            disabled: choices.series.length === 0,
          },
          { value: "sermon", label: "One sermon", disabled: choices.sermons.length === 0 },
        ]}
        value={mode}
        onChange={(next) => {
          if (next === "series")
            onChange({ mode: "series", seriesId: choices.series[0]?.id ?? "" });
          else if (next === "sermon")
            onChange({ mode: "sermon", sermonId: choices.sermons[0]?.id ?? "" });
          else onChange({ mode: "latest" });
        }}
      />
      {mode === "series" ? (
        <Select
          label="Series"
          disabled={disabled}
          allowDeselect={false}
          data={choices.series.map((c) => ({ value: c.id, label: c.label }))}
          value={str(source["seriesId"]) || null}
          onChange={(next) => {
            if (next !== null) onChange({ mode: "series", seriesId: next });
          }}
        />
      ) : null}
      {mode === "sermon" ? (
        <Select
          label="Sermon"
          searchable
          disabled={disabled}
          allowDeselect={false}
          data={choices.sermons.map((c) => ({ value: c.id, label: c.label }))}
          value={str(source["sermonId"]) || null}
          onChange={(next) => {
            if (next !== null) onChange({ mode: "sermon", sermonId: next });
          }}
        />
      ) : null}
    </Stack>
  );
}

/**
 * Which zone a block's times are in: the church's, or one campus's (ADR 0033, second amendment of 2026-10-03). A
 * church with no campuses sees nothing; a campus that has since gone is named as such, and the site keeps the
 * church's zone for it.
 */
function CampusControl({
  label,
  value,
  onChange,
  disabled,
  context,
}: {
  readonly label: string;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly disabled: boolean;
  readonly context: BlockFieldsContext;
}) {
  const selected = typeof value === "string" ? value : null;
  if (context.campuses.length === 0 && selected === null) return null;
  const church = `The church's time zone (${context.churchTimeZone})`;
  const data = [
    { value: "", label: church },
    ...context.campuses.map((c) => ({
      value: c.id,
      label: `${c.name} (${c.timeZone ?? context.churchTimeZone})`,
    })),
    ...(selected !== null && !context.campuses.some((c) => c.id === selected)
      ? [
          {
            value: selected,
            label: "A campus that is no longer active (the church's zone is used)",
          },
        ]
      : []),
  ];
  return (
    <Select
      label={label}
      description="For a campus streaming in another time zone. The times below are read in this zone."
      disabled={disabled}
      allowDeselect={false}
      data={data}
      value={selected ?? ""}
      onChange={(next) => {
        onChange(next === null || next === "" ? undefined : next);
      }}
    />
  );
}

/**
 * The stream address. What staff pasted is reduced to an address (an embed code keeps only its `src`), the provider
 * follows the address when it plainly belongs to another one, and a YouTube handle's channel is found once, here,
 * so the block stores the channel id and the church's page never asks YouTube while a visitor waits.
 */
function StreamControl({
  field,
  block,
  onBlockChange,
  disabled,
  context,
}: {
  readonly field: Extract<Field, { kind: "stream" }>;
  readonly block: Values;
  readonly onBlockChange: (next: Values) => void;
  readonly disabled: boolean;
  readonly context: BlockFieldsContext;
}) {
  const url = str(block[field.key]);
  const provider: LivestreamProvider | null =
    typeof block["provider"] === "string" && block["provider"] in LIVESTREAM_PROVIDER_LABELS
      ? (block["provider"] as LivestreamProvider)
      : null;
  const channelId = typeof block["channelId"] === "string" ? block["channelId"] : undefined;
  const handle =
    provider === "youtube" && isLivestreamAddress("youtube", url) ? youtubeHandle(url) : null;
  const find = context.findYoutubeChannel;
  const [finding, setFinding] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ address: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The answer is applied to the block as it is when the answer arrives, by the finder the form has then.
  const latest = useRef({ block, onBlockChange, find });
  latest.current = { block, onBlockChange, find };

  // A handle with no channel yet is looked up as soon as it is a valid address: once per address, again on request.
  const target =
    handle !== null &&
    channelId === undefined &&
    find !== undefined &&
    !disabled &&
    failure?.address !== url
      ? url
      : null;
  useEffect(() => {
    const finder = latest.current.find;
    if (target === null || finder === undefined) return;
    let current = true;
    setFinding(target);
    finder(target).then(
      (id) => {
        if (!current) return;
        setFinding(null);
        latest.current.onBlockChange({ ...latest.current.block, channelId: id });
      },
      (error: unknown) => {
        if (!current) return;
        setFinding(null);
        setFailure({
          address: target,
          message: error instanceof Error ? error.message : "We could not reach YouTube.",
        });
      },
    );
    return () => {
      current = false;
    };
  }, [target, attempt]);

  const problem = streamAddressProblem(url, block);
  const linkOnly =
    provider !== null &&
    problem === null &&
    url !== "" &&
    isLinkOnlyAddress(provider, url, channelId);
  const failed = failure !== null && failure.address === url ? failure.message : null;
  const error =
    problem ??
    failed ??
    (handle !== null && channelId === undefined && find === undefined
      ? "Paste your channel's own address instead (https://www.youtube.com/channel/UC…/live): a handle needs its channel found, which is not available here."
      : null);

  return (
    <Stack gap={4}>
      <TextInput
        label={field.label}
        required
        disabled={disabled}
        description={
          provider === null
            ? "Paste the stream's address from your provider."
            : STREAM_ADDRESS_HELP[provider]
        }
        {...(error === null ? {} : { error })}
        value={url}
        onChange={(event) => {
          const raw = event.currentTarget.value;
          // An embed code keeps only its address; a pasted address from another provider switches the provider.
          const next =
            raw.includes("<") || raw.includes("facebook.com/plugins/")
              ? streamAddressFromPaste(raw)
              : raw;
          const owner = livestreamProviderOf(next.trim());
          const changed: Values = { ...block, [field.key]: next };
          // Never to a provider that is coming soon: the address stays, and the field says it is coming soon.
          if (owner !== null && owner !== provider && isLivestreamProviderAvailable(owner)) {
            changed["provider"] = owner;
          }
          // A channel belongs to one handle: an address with another handle, or none, forgets it.
          if (youtubeHandle(next.trim()) !== youtubeHandle(url.trim())) {
            delete changed["channelId"];
          }
          onBlockChange(changed);
        }}
      />
      {handle !== null && channelId !== undefined ? (
        <Text size="sm" c="teal" role="status">
          Found: channel {channelId}
        </Text>
      ) : null}
      {finding !== null && finding === url ? (
        <Text size="sm" c="dimmed" role="status">
          Finding @{handle} on YouTube…
        </Text>
      ) : null}
      {failed !== null && find !== undefined ? (
        <Button
          variant="light"
          size="xs"
          style={{ alignSelf: "flex-start" }}
          disabled={disabled}
          onClick={() => {
            setFailure(null);
            setAttempt((n) => n + 1);
          }}
        >
          Find channel again
        </Button>
      ) : null}
      {linkOnly && provider === "facebook" ? (
        <Text size="sm" c="dimmed">
          Facebook does not let other websites play a page&apos;s live stream, so visitors get a
          Watch live on Facebook button. To play it in the page, paste that week&apos;s video
          address.
        </Text>
      ) : null}
    </Stack>
  );
}

function LinkControl({
  field,
  value,
  onChange,
  disabled,
}: Omit<FieldControlProps, "assets"> & { readonly field: Extract<Field, { kind: "link" }> }) {
  const link = (value ?? {}) as { label?: string; href?: string };
  const set = (key: "label" | "href", next: string) => {
    const updated = { ...link, [key]: next };
    // Both parts empty means no link at all, which an optional button allows.
    onChange((updated.label ?? "") === "" && (updated.href ?? "") === "" ? undefined : updated);
  };
  return (
    <Fieldset legend={field.label} variant="unstyled" styles={LEGEND}>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <TextInput
          label="Button text"
          required={field.required === true}
          disabled={disabled}
          value={link.label ?? ""}
          onChange={(event) => {
            const next = event.currentTarget.value;
            set("label", next);
          }}
        />
        <TextInput
          label="Goes to"
          description="A page such as /plan-a-visit, or an https:// address"
          required={field.required === true}
          disabled={disabled}
          value={link.href ?? ""}
          onChange={(event) => {
            const next = event.currentTarget.value;
            set("href", next);
          }}
        />
      </SimpleGrid>
    </Fieldset>
  );
}

function ImageControl({
  field,
  value,
  onChange,
  assets,
  disabled,
}: FieldControlProps & { readonly field: Extract<Field, { kind: "image" }> }) {
  const image = value as { path?: string; alt?: string; decorative?: boolean } | undefined;
  return (
    <Fieldset legend={field.label} variant="unstyled" styles={LEGEND}>
      <Stack gap="xs">
        <Select
          aria-label={`${field.label}: choose from your images`}
          placeholder={assets.length === 0 ? "No images uploaded yet" : "Choose from your images"}
          disabled={disabled || assets.length === 0}
          clearable
          searchable
          data={assets.map((a) => ({ value: a.storage_path, label: a.alt_text }))}
          value={image?.path ?? null}
          onChange={(path) => {
            if (path === null) {
              onChange(undefined);
              return;
            }
            const asset = assets.find((a) => a.storage_path === path);
            onChange({
              path,
              alt: asset?.alt_text ?? "",
              decorative: false,
              ...(asset?.width == null ? {} : { width: asset.width }),
              ...(asset?.height == null ? {} : { height: asset.height }),
            });
          }}
        />
        {image?.path === undefined ? null : (
          <>
            <TextInput
              label="Description for people who cannot see it"
              disabled={disabled || image.decorative === true}
              value={image.alt ?? ""}
              onChange={(event) => {
                const next = event.currentTarget.value;
                onChange({ ...image, alt: next });
              }}
            />
            <Checkbox
              label="Decorative only, nothing to describe"
              disabled={disabled}
              checked={image.decorative === true}
              onChange={(event) => {
                const next = event.currentTarget.checked;
                onChange({ ...image, decorative: next });
              }}
            />
          </>
        )}
      </Stack>
    </Fieldset>
  );
}

/**
 * Rich text is typed with a few shortcuts and stored as structured nodes (ADR 0033). The typed text is kept as the
 * editor's own state, so converting on every keystroke never moves the cursor or eats a trailing space.
 */
function RichTextControl({
  label,
  value,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly disabled: boolean;
}) {
  const incoming = Array.isArray(value) ? richTextToMarkup(value as RichText) : "";
  const [text, setText] = useState(incoming);
  // When the value changes from outside (an item moved, a version restored), show it. Both sides are compared in the
  // same normalised form, so the text staff are typing is never replaced by its own reading.
  const normal = (markup: string) => JSON.stringify(richTextFromMarkup(markup));
  if (normal(text) !== normal(incoming)) setText(incoming);
  return (
    <Textarea
      label={label}
      description="Blank line between paragraphs. ## heading, - list item, 1. numbered item, > quote, **bold**, *italic*, [link text](/page)."
      disabled={disabled}
      autosize
      minRows={5}
      value={text}
      onChange={(event) => {
        const next = event.currentTarget.value;
        setText(next);
        onChange(richTextFromMarkup(next));
      }}
    />
  );
}

function ListControl({
  field,
  value,
  onChange,
  assets,
  forms,
  disabled,
  context,
}: FieldControlProps & { readonly field: Extract<Field, { kind: "list" }> }) {
  const items = Array.isArray(value) ? (value as Values[]) : [];
  const move = (from: number, to: number) => {
    const next = [...items];
    const [moved] = next.splice(from, 1);
    if (moved !== undefined) next.splice(to, 0, moved);
    onChange(next);
  };
  return (
    <Fieldset legend={field.label}>
      <Stack gap="md">
        {items.map((item, index) => (
          // Items have no id of their own; the position is their identity while the block is being edited.
          <Stack key={index} gap="xs">
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" fw={600}>
                {field.itemLabel} {index + 1}
              </Text>
              <Group gap={4} wrap="nowrap">
                <ActionIcon
                  variant="subtle"
                  aria-label={`Move ${field.itemLabel.toLowerCase()} ${String(index + 1)} up`}
                  disabled={disabled || index === 0}
                  onClick={() => {
                    move(index, index - 1);
                  }}
                >
                  <IconArrowUp size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  aria-label={`Move ${field.itemLabel.toLowerCase()} ${String(index + 1)} down`}
                  disabled={disabled || index === items.length - 1}
                  onClick={() => {
                    move(index, index + 1);
                  }}
                >
                  <IconArrowDown size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={`Remove ${field.itemLabel.toLowerCase()} ${String(index + 1)}`}
                  disabled={disabled}
                  onClick={() => {
                    onChange(items.filter((_, i) => i !== index));
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
            <BlockFields
              fields={field.fields}
              value={item}
              assets={assets}
              forms={forms}
              disabled={disabled}
              {...(context === undefined ? {} : { context })}
              onChange={(next) => {
                onChange(items.map((existing, i) => (i === index ? next : existing)));
              }}
            />
          </Stack>
        ))}
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          disabled={disabled || items.length >= field.max}
          onClick={() => {
            onChange([...items, emptyItem(field.fields)]);
          }}
          style={{ alignSelf: "flex-start" }}
        >
          Add {field.itemLabel.toLowerCase()}
        </Button>
      </Stack>
    </Fieldset>
  );
}
